/**
 * Template Center Modal — Phase 1 (real backend data only)
 *
 * Hard rules enforced:
 *  HR1: No blank-project fallback. Only POST /templates/:id/instantiate-v5_1.
 *  HR2: v1 templates only (uses listTemplates from templates.api.ts).
 *  HR4: Menu label is the canonical NEW_TEMPLATE_ACTION_LABEL constant
 *       from `features/templates/labels.ts` (set in SidebarWorkspaces).
 *  HR5: "Created by me" hidden until backed by real data.
 *  HR6: Every card is a real backend template — no hardcoded placeholders.
 */
import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import { X, Search, LayoutGrid, Building2, Layers, AlertCircle, User, Users as UsersIcon, Star } from "lucide-react";
import { toast } from "sonner";

import { useWorkspaceStore } from "@/state/workspace.store";
import { useWorkspaceRole } from "@/hooks/useWorkspaceRole";
import { useAuth } from "@/state/AuthContext";
import { listTemplates, setTemplatePreferred, type TemplateDto } from "@/features/templates/templates.api";
import { getPreview, type PreviewResponse } from "@/features/templates/api";
import {
  deriveSetupLevel,
  isOrgPreferredTemplate,
  matchesKindFilter,
  resolveCatalogDepartment,
  resolveDefaultCatalogDepartment,
  resolvePostInstantiateProjectPath,
  sortTemplatesFeaturedFirst,
  type TemplateKindFilter,
  type TemplateSetupLevel,
} from "@/features/templates/template.mapper";
import { isPlatformAdmin } from "@/utils/access";
import { modKeyLabel } from "@/utils/modKeyLabel";
import { pluralize } from "@/utils/pluralize";
import { useDialogBehavior } from "@/hooks/useDialogBehavior";
import {
  ORG_TEMPLATE_LABEL,
  PREFERRED_SHELF_EMPTY_GRID_COPY,
  TEMPLATE_INSTANTIATE_DENIED_COPY,
} from "@/features/templates/templateCenterCopy";
import { TemplatePreviewModal } from "./TemplatePreviewModal";
import { UseTemplateFlowModal } from "./UseTemplateFlowModal";
import { AttachDocumentModal } from "./AttachDocumentModal";
import { TemplateBlueprint } from "./TemplateBlueprint";
import {
  DEPARTMENT_CATALOG_CATEGORIES,
} from "@/features/templates/categories";

type UseFlowInitialStep = "blueprint" | "name";

interface TemplateCenterModalProps {
  open: boolean;
  onClose: () => void;
  workspaceId: string;
  /** TC-F1: render as full-page browse (no portal/backdrop) for /templates route. */
  embedded?: boolean;
  /** TC-F3: open on a catalog tier (e.g. "Your templates"). */
  initialCategory?: string | null;
}

type TopView = "all" | "by-zephix" | "workspace" | "mine";

/**
 * Phase 5A — secondary "source" filters. The primary IA is category-first;
 * source filters are a small section under categories. They render only
 * when they would be useful (i.e. when at least one template matches).
 */
const SOURCE_VIEWS: { id: TopView; label: string; icon: React.ElementType }[] = [
  { id: "all", label: "All templates", icon: LayoutGrid },
  { id: "by-zephix", label: "Created by Zephix", icon: Building2 },
  { id: "workspace", label: "Workspace templates", icon: UsersIcon },
  { id: "mine", label: "Created by me", icon: User },
];

/**
 * Department-first browse grouping (Starters / Marketing / Product & Eng / …).
 * Document/form kinds keep a flat list under their type filter.
 */
function groupByDepartment(templates: TemplateDto[]): Map<string, TemplateDto[]> {
  const groups = new Map<string, TemplateDto[]>();
  for (const tpl of templates) {
    const key = resolveCatalogDepartment(tpl);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(tpl);
  }
  return groups;
}

/** Setup badge — derived formula only (TEMPLATE-UX-1). */
function deriveComplexity(tpl: TemplateDto): TemplateSetupLevel {
  return deriveSetupLevel(tpl);
}

/** Phase 5A — read purpose from typed metadata, fall back to description. */
function templatePurpose(tpl: TemplateDto): string {
  const meta = tpl.metadata as Record<string, unknown> | null | undefined;
  const purpose = meta && typeof meta.purpose === 'string' ? meta.purpose : '';
  if (purpose.trim()) return purpose.trim();
  // Fallback: first sentence of the description
  const desc = (tpl.description ?? '').trim();
  if (!desc) return '';
  const firstSentence = desc.split(/(?<=\.)\s/)[0] ?? desc;
  return firstSentence;
}

/**
 * Phase 4 (Template Center): per-card source label.
 * - SYSTEM templates → "Zephix"
 * - WORKSPACE templates created by current user → "Mine"
 * - WORKSPACE templates by others → "Workspace"
 * - ORG → "Organization"
 */
function sourceLabel(tpl: TemplateDto, currentUserId: string | null): string {
  if (tpl.templateScope === "SYSTEM") return "Zephix";
  if (tpl.templateScope === "ORG") return ORG_TEMPLATE_LABEL;
  if (tpl.templateScope === "WORKSPACE") {
    if (currentUserId && tpl.createdById === currentUserId) return "Mine";
    return "Workspace";
  }
  return tpl.templateScope;
}

export function TemplateCenterModal({
  open,
  onClose,
  workspaceId,
  embedded = false,
  initialCategory = null,
}: TemplateCenterModalProps) {
  const navigate = useNavigate();
  const { activeWorkspaceId, setActiveWorkspace } = useWorkspaceStore();
  const resolvedWorkspaceId = workspaceId ?? activeWorkspaceId;
  const { canWrite, isReadOnly, loading: roleLoading } = useWorkspaceRole(
    resolvedWorkspaceId,
  );
  const { user } = useAuth();
  const currentUserId = (user as any)?.id ?? null;
  const isAdmin = isPlatformAdmin(user);
  /**
   * Instantiate requires workspace write (backend requireWorkspaceWrite).
   * Must use useWorkspaceRole — store.canWrite has no production setter and
   * was permanently false (FIX-USE-TEMPLATE-BUTTON-DISABLED-1).
   */
  const canInstantiate = Boolean(canWrite && !isReadOnly && !roleLoading);

  const [templates, setTemplates] = useState<TemplateDto[]>([]);
  // Start true so the first paint never flashes "No templates in this catalog"
  // before the fetch effect runs (TEMPLATE-CENTER-REDESIGN-1 finding #5).
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [activeView, setActiveView] = useState<TopView | null>(null);
  const [activeCategory, setActiveCategory] = useState<string | null>(initialCategory);
  const [search, setSearch] = useState("");
  const [kindFilter, setKindFilter] = useState<TemplateKindFilter>("projects");
  /** TEMPLATE-CENTER-REDESIGN-1 — setup complexity filter (derived formula). */
  const [complexityFilter, setComplexityFilter] = useState<
    'all' | TemplateSetupLevel
  >('all');

  /** Document-kind preview only — project Preview opens UseTemplateFlowModal (blueprint). */
  const [previewTemplate, setPreviewTemplate] = useState<TemplateDto | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<{ code: string; message: string } | null>(null);
  const [previewData, setPreviewData] = useState<PreviewResponse | null>(null);

  const [pendingTemplate, setPendingTemplate] = useState<TemplateDto | null>(null);
  const [flowInitialStep, setFlowInitialStep] = useState<UseFlowInitialStep>("blueprint");
  const [attachTemplate, setAttachTemplate] = useState<TemplateDto | null>(null);
  const [instantiateError, setInstantiateError] = useState<{ code: string; message: string } | null>(null);
  const [preferredBusyId, setPreferredBusyId] = useState<string | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const nestedDialogOpen = Boolean(previewTemplate || pendingTemplate || attachTemplate);
  const dialogRef = useDialogBehavior(open && !embedded && !nestedDialogOpen, onClose);

  const focusSearch = useCallback(() => {
    searchInputRef.current?.focus();
    searchInputRef.current?.select();
  }, []);

  // While Template Center is open, ⌘K focuses Center search (CommandPalette yields).
  useEffect(() => {
    if (!open && !embedded) return;
    document.body.setAttribute("data-template-center-open", "true");
    const onKey = (e: KeyboardEvent) => {
      const k = (e.key ?? "").toLowerCase();
      const mod = e.metaKey || e.ctrlKey;
      if (mod && k === "k") {
        e.preventDefault();
        e.stopPropagation();
        focusSearch();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => {
      document.body.removeAttribute("data-template-center-open");
      window.removeEventListener("keydown", onKey, true);
    };
  }, [open, embedded, focusSearch]);

  useEffect(() => {
    if ((!open && !embedded) || !workspaceId) return;
    if (workspaceId && activeWorkspaceId !== workspaceId) {
      setActiveWorkspace(workspaceId);
    }
  }, [open, embedded, workspaceId, activeWorkspaceId, setActiveWorkspace]);

  // Phase 4.6: extracted so we can also call it from the
  // 'zephix:templates:invalidate' event listener below.
  // Cleanup cancels in-flight applies when workspace/header context changes.
  const fetchTemplatesRef = React.useRef<() => (() => void) | void>(() => {});
  useEffect(() => {
    fetchTemplatesRef.current = () => {
      let cancelled = false;
      setLoadingTemplates(true);
      setLoadError(null);
      listTemplates()
        .then((rows) => {
          if (cancelled) return;
          const active = sortTemplatesFeaturedFirst((rows || []).filter((t) => t.isActive));
          setTemplates(active);
          if (active.length > 0 && !activeCategory) {
            const scoped = groupByDepartment(active.filter((t) => matchesKindFilter(t, 'projects')));
            const available = DEPARTMENT_CATALOG_CATEGORIES.filter(
              (cat) => (scoped.get(cat)?.length ?? 0) > 0,
            );
            const preferred = resolveDefaultCatalogDepartment(available, initialCategory);
            if (preferred) setActiveCategory(preferred);
          }
        })
        .catch((err) => {
          if (cancelled) return;
          setLoadError(err?.message || "Failed to load templates");
          setTemplates([]);
        })
        .finally(() => {
          if (!cancelled) setLoadingTemplates(false);
        });
      return () => {
        cancelled = true;
      };
    };
  }, [activeCategory, initialCategory]);

  // Fetch when Center opens AND when resolved workspace changes so
  // `x-workspace-id` on listTemplates() matches the modal target (not a stale store).
  useEffect(() => {
    if (!open && !embedded) return;
    // Wait until store catches up when a prop workspaceId is provided.
    // Keep the skeleton up — never flash the empty-catalog message.
    if (workspaceId && activeWorkspaceId !== workspaceId) {
      setLoadingTemplates(true);
      return;
    }
    return fetchTemplatesRef.current();
  }, [open, embedded, workspaceId, activeWorkspaceId, resolvedWorkspaceId]);

  useEffect(() => {
    if (!open && !embedded) return;
    const handler = () => fetchTemplatesRef.current();
    window.addEventListener("zephix:templates:invalidate", handler);
    return () =>
      window.removeEventListener("zephix:templates:invalidate", handler);
  }, [open, embedded]);

  // Department-first browse for projects only. Documents stay a flat catalog.
  // Founder ruling: no "upcoming"/coming-soon shelf — only working templates.
  const isProjectBrowse = kindFilter === 'projects';
  const kindFilteredTemplates = useMemo(
    () =>
      templates.filter((t) => {
        if (!matchesKindFilter(t, kindFilter)) return false;
        if (t.kind !== 'document' && t.comingSoon === true) return false;
        return true;
      }),
    [templates, kindFilter],
  );
  const preferredOrgTemplates = useMemo(
    () => kindFilteredTemplates.filter(isOrgPreferredTemplate),
    [kindFilteredTemplates],
  );
  const categories = useMemo(() => {
    if (!isProjectBrowse) return [];
    const ordered: string[] = [];
    const scoped = groupByDepartment(kindFilteredTemplates);
    for (const cat of DEPARTMENT_CATALOG_CATEGORIES) {
      if ((scoped.get(cat)?.length ?? 0) > 0) ordered.push(cat);
    }
    return ordered;
  }, [kindFilteredTemplates, isProjectBrowse]);

  const kindScopedGroups = useMemo(
    () => (isProjectBrowse ? groupByDepartment(kindFilteredTemplates) : new Map<string, TemplateDto[]>()),
    [kindFilteredTemplates, isProjectBrowse],
  );

  // Source filters are kind-scoped (don't show project counts on Documents).
  // Preferred rows live on the shelf, so they are not part of these counts.
  const countedTemplates = kindFilteredTemplates.filter((t) => !isOrgPreferredTemplate(t));
  const sourceViewsCounts: Record<TopView, number> = {
    all: countedTemplates.length,
    'by-zephix': countedTemplates.filter((t) => t.templateScope === 'SYSTEM').length,
    workspace: countedTemplates.filter((t) => t.templateScope === 'WORKSPACE').length,
    mine: currentUserId
      ? countedTemplates.filter(
          (t) => t.templateScope === 'WORKSPACE' && t.createdById === currentUserId,
        ).length
      : 0,
  };
  const visibleSourceViews = SOURCE_VIEWS.filter(
    (v) => sourceViewsCounts[v.id] > 0,
  );

  // Determine display set based on active source view or active category
  let displayLabel = "";
  let displayTemplates: TemplateDto[] = [];

  if (!isProjectBrowse) {
    // Documents: flat list. Sources still apply; department aisles do not.
    if (activeView === "by-zephix") {
      displayLabel = "Created by Zephix";
      displayTemplates = kindFilteredTemplates.filter((t) => t.templateScope === "SYSTEM");
    } else if (activeView === "workspace") {
      displayLabel = "Workspace templates";
      displayTemplates = kindFilteredTemplates.filter((t) => t.templateScope === "WORKSPACE");
    } else if (activeView === "mine") {
      displayLabel = "Created by me";
      displayTemplates = currentUserId
        ? kindFilteredTemplates.filter(
            (t) => t.templateScope === "WORKSPACE" && t.createdById === currentUserId,
          )
        : [];
    } else {
      displayLabel = "Documents";
      displayTemplates = kindFilteredTemplates;
    }
  } else if (activeView === "all") {
    displayLabel = "All templates";
    displayTemplates = kindFilteredTemplates;
  } else if (activeView === "by-zephix") {
    displayLabel = "Created by Zephix";
    displayTemplates = kindFilteredTemplates.filter((t) => t.templateScope === "SYSTEM");
  } else if (activeView === "workspace") {
    displayLabel = "Workspace templates";
    displayTemplates = kindFilteredTemplates.filter((t) => t.templateScope === "WORKSPACE");
  } else if (activeView === "mine") {
    displayLabel = "Created by me";
    displayTemplates = currentUserId
      ? kindFilteredTemplates.filter(
          (t) => t.templateScope === "WORKSPACE" && t.createdById === currentUserId,
        )
      : [];
  } else if (activeCategory) {
    displayLabel = activeCategory;
    displayTemplates = kindScopedGroups.get(activeCategory) || [];
  } else if (categories.length > 0) {
    const first = categories[0];
    displayLabel = first;
    displayTemplates = kindScopedGroups.get(first) || [];
  } else {
    displayLabel = "All templates";
    displayTemplates = kindFilteredTemplates;
  }

  // Auto-pick featured department (pm_waterfall_v2 → Operations & PMO) once project data loads.
  useEffect(() => {
    if (
      isProjectBrowse &&
      !loadingTemplates &&
      activeView === null &&
      activeCategory === null &&
      categories.length > 0
    ) {
      setActiveCategory(resolveDefaultCatalogDepartment(categories, initialCategory));
    }
  }, [loadingTemplates, activeView, activeCategory, categories, isProjectBrowse, initialCategory]);

  // Apply search + complexity filter (projects only; documents have no setup badge).
  const filteredTemplates = (() => {
    let rows = displayTemplates;
    if (isProjectBrowse && complexityFilter !== 'all') {
      rows = rows.filter((t) => deriveSetupLevel(t) === complexityFilter);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      rows = rows.filter(
        (t) =>
          t.name.toLowerCase().includes(q) ||
          (t.description || "").toLowerCase().includes(q) ||
          resolveCatalogDepartment(t).toLowerCase().includes(q),
      );
    }
    return rows;
  })();

  // Preferred templates sit on the shelf, so they are not in the department grid
  // unless a search is showing them there. Counts follow the grid.
  const gridTemplates = filteredTemplates.filter(
    (tpl) => search.trim() || !isOrgPreferredTemplate(tpl),
  );
  const emptiedByPreferredShelf =
    !search.trim() &&
    filteredTemplates.length > 0 &&
    gridTemplates.length === 0 &&
    filteredTemplates.every(isOrgPreferredTemplate);

  const countShown = (rows: TemplateDto[] | undefined) =>
    (rows ?? []).filter((tpl) => search.trim() || !isOrgPreferredTemplate(tpl)).length;

  // Preview: projects → unified Use flow (blueprint step); documents → preview modal.
  const handlePreview = async (tpl: TemplateDto) => {
    if (tpl.kind === "document") {
      setPreviewTemplate(tpl);
      setPreviewLoading(true);
      setPreviewError(null);
      setPreviewData(null);
      try {
        const data = await getPreview(tpl.id);
        setPreviewData(data);
      } catch (err: any) {
        setPreviewError({
          code: err?.response?.data?.code || "PREVIEW_FAILED",
          message: err?.response?.data?.message || err?.message || "Failed to load template preview",
        });
      } finally {
        setPreviewLoading(false);
      }
      return;
    }
    if (!tpl.id) {
      toast.error("This template cannot be used yet");
      return;
    }
    setFlowInitialStep("blueprint");
    setPendingTemplate(tpl);
  };

  const handleClosePreview = () => {
    setPreviewTemplate(null);
    setPreviewData(null);
    setPreviewError(null);
    setPreviewLoading(false);
  };

  // Document catalog: open as-is (preview). Project attach is optional secondary.
  const handleUseTemplate = (tpl: TemplateDto) => {
    if (tpl.kind === 'document') {
      if (!tpl.id) {
        toast.error('This template cannot be opened yet');
        return;
      }
      void handlePreview(tpl);
      return;
    }
    if (!canInstantiate) {
      toast.info(TEMPLATE_INSTANTIATE_DENIED_COPY);
      return;
    }
    if (!tpl.id) {
      toast.error("This template cannot be used yet");
      return;
    }
    setFlowInitialStep("name");
    setPendingTemplate(tpl);
  };

  const handlePreferredToggle = async (tpl: TemplateDto) => {
    if (!isAdmin || tpl.templateScope !== 'ORG') return;
    setPreferredBusyId(tpl.id);
    try {
      const next = !(tpl.isPreferred === true);
      const updated = await setTemplatePreferred(tpl.id, next);
      setTemplates((prev) =>
        prev.map((t) =>
          t.id === tpl.id
            ? { ...t, isPreferred: updated.isPreferred ?? next }
            : t,
        ),
      );
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } }; message?: string };
      toast.error(e?.response?.data?.message || e?.message || 'Could not update preferred');
    } finally {
      setPreferredBusyId(null);
    }
  };

  const handleFlowSuccess = (projectId: string, tpl: TemplateDto) => {
    setPendingTemplate(null);
    setInstantiateError(null);
    handleClosePreview();
    onClose();
    toast.success('Project created');
    const wsId = workspaceId || activeWorkspaceId;
    if (wsId) {
      window.dispatchEvent(
        new CustomEvent('zephix:projects:invalidate', {
          detail: { workspaceId: wsId, projectId },
        }),
      );
    }
    navigate(resolvePostInstantiateProjectPath(tpl, projectId), { replace: true });
  };

  if (!open && !embedded) return null;

  const shell = (
    <>
      {/* Modal / embedded shell */}
      <div
        ref={embedded ? undefined : dialogRef}
        data-testid="template-center-shell"
        role={embedded ? undefined : "dialog"}
        aria-modal={embedded ? undefined : true}
        aria-labelledby="template-center-title"
        tabIndex={embedded ? undefined : -1}
        className={
          embedded
            ? "flex min-h-[calc(100vh-8rem)] flex-col overflow-hidden rounded-xl border border-slate-200 bg-slate-50 shadow-sm"
            : "fixed inset-0 z-[5001] m-auto flex flex-col overflow-hidden rounded-xl border border-slate-200 bg-slate-50 shadow-sm"
        }
        style={embedded ? undefined : { width: "min(96vw, 1280px)", height: "min(92vh, 900px)" }}
      >
        {/* Header — spotlight search */}
        <div className="flex h-14 shrink-0 items-center justify-between border-b border-slate-200/80 bg-white px-6">
          <div className="flex items-center gap-3">
            <Layers className="h-5 w-5 text-blue-600" />
            <h2 id="template-center-title" className="text-lg font-semibold text-slate-900">Templates</h2>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex rounded-lg border border-slate-200 p-0.5" data-testid="template-kind-filter">
              {(
                [
                  { id: 'projects' as const, label: 'Projects' },
                  { id: 'documents' as const, label: 'Documents' },
                ] as const
              ).map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => {
                    setKindFilter(opt.id);
                    setSearch('');
                    setComplexityFilter('all');
                    if (opt.id === 'documents') {
                      // Documents are a flat catalog — do not keep a Projects department selection.
                      setActiveCategory(null);
                      setActiveView('all');
                    } else {
                      setActiveView(null);
                      setActiveCategory(null);
                    }
                  }}
                  className={`rounded-md px-3 py-1 text-xs font-medium transition-colors duration-150 ${
                    kindFilter === opt.id
                      ? 'bg-blue-600 text-white'
                      : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <label htmlFor="template-center-search" className="sr-only">
                Search templates
              </label>
              <input
                ref={searchInputRef}
                id="template-center-search"
                type="search"
                aria-label="Search templates"
                placeholder="Search templates"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                }}
                className="w-72 rounded-lg border border-slate-200 bg-slate-50 py-1.5 pl-9 pr-14 text-sm text-slate-700 placeholder:text-slate-400 transition-shadow duration-150 focus:border-slate-300 focus:bg-white focus:outline-none focus:ring-2 focus:ring-slate-200"
                data-testid="template-center-search"
              />
              <div className="pointer-events-none absolute right-2 top-1/2 flex -translate-y-1/2 items-center gap-0.5 opacity-60">
                <kbd className="rounded border border-slate-200 bg-white px-1 font-mono text-[10px] text-slate-500">{modKeyLabel()}</kbd>
                <kbd className="rounded border border-slate-200 bg-white px-1 font-mono text-[10px] text-slate-500">K</kbd>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-1.5 text-slate-400 transition-colors duration-150 hover:bg-slate-100 hover:text-slate-600"
              aria-label={embedded ? 'Back' : 'Close'} title={embedded ? 'Back' : 'Close'}
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex flex-1 overflow-hidden">
          {/* Left sidebar — Phase 5A: category-first IA, sources secondary */}
          <aside
            className="w-56 shrink-0 overflow-y-auto border-r border-slate-200 bg-slate-50 py-4"
            data-testid="template-center-rail"
          >
            {/* PRIMARY: Departments — projects only (documents are flat) */}
            {isProjectBrowse ? (
              <>
            <div className="px-4 pb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
              Departments
            </div>
            <nav className="space-y-0.5 px-2 pb-3" data-testid="template-center-categories">
              {categories.length === 0 && (
                <div className="px-3 py-2 text-xs text-slate-400">
                  No categories yet
                </div>
              )}
              {categories.map((cat) => {
                const count = countShown(kindScopedGroups.get(cat));
                const isActive = activeView === null && cat === activeCategory;
                return (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => {
                      setActiveView(null);
                      setActiveCategory(cat);
                      setSearch("");
                    }}
                    className={`flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm transition-colors duration-150 ease-in ${
                      isActive ? "bg-white font-medium text-slate-900 shadow-sm ring-1 ring-slate-200/80" : "text-slate-600 hover:bg-white/70 hover:text-slate-900"
                    }`}
                    data-testid={`template-center-category-${cat}`}
                  >
                    <Layers className={`h-4 w-4 shrink-0 ${isActive ? "text-blue-600" : "text-slate-400"}`} />
                    <span className="min-w-0 flex-1">{cat}</span>
                    <span className="text-[10px] font-semibold text-slate-400">{count}</span>
                  </button>
                );
              })}
            </nav>
              </>
            ) : null}

            {/* SECONDARY: source filters — only render non-empty ones */}
            {visibleSourceViews.length > 0 && (
              <>
                <div className="mx-4 mb-3 border-t border-slate-200" />
                <div className="px-4 pb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                  Sources
                </div>
                <nav className="space-y-0.5 px-2" data-testid="template-center-sources">
                  {visibleSourceViews.map((view) => {
                    const Icon = view.icon;
                    const isActive = activeView === view.id;
                    return (
                      <button
                        key={view.id}
                        type="button"
                        onClick={() => {
                          setActiveView(view.id);
                          setActiveCategory(null);
                          setSearch("");
                        }}
                        className={`flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm transition-colors duration-150 ease-in ${
                          isActive ? "bg-white font-medium text-slate-900 shadow-sm ring-1 ring-slate-200/80" : "text-slate-600 hover:bg-white/70 hover:text-slate-900"
                        }`}
                        data-testid={`template-center-source-${view.id}`}
                      >
                        <Icon className={`h-4 w-4 shrink-0 ${isActive ? "text-blue-600" : "text-slate-400"}`} />
                        <span className="min-w-0 flex-1 truncate">{view.label}</span>
                        <span className="text-[10px] font-semibold text-slate-400">
                          {sourceViewsCounts[view.id]}
                        </span>
                      </button>
                    );
                  })}
                </nav>
              </>
            )}
          </aside>

          {/* Right panel — template cards (stable padding/gap — no search jitter) */}
          <main className="flex min-h-0 flex-1 flex-col overflow-y-auto bg-slate-50 p-6">
            {loadingTemplates ? (
              <div data-testid="template-center-loading" aria-busy="true" aria-live="polite">
                <div className="mb-5 min-h-[2.75rem]">
                  <div className="h-4 w-40 animate-pulse rounded bg-slate-200" />
                  <div className="mt-2 h-3 w-24 animate-pulse rounded bg-slate-100" />
                </div>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                  {[0, 1, 2, 3, 4, 5].map((i) => (
                    <div
                      key={i}
                      className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm"
                      data-testid="template-card-skeleton"
                    >
                      <div className="h-28 animate-pulse bg-slate-100" />
                      <div className="space-y-2 p-4">
                        <div className="h-3.5 w-3/4 animate-pulse rounded bg-slate-200" />
                        <div className="h-3 w-full animate-pulse rounded bg-slate-100" />
                        <div className="h-3 w-2/3 animate-pulse rounded bg-slate-100" />
                        <div className="mt-3 flex gap-2">
                          <div className="h-5 w-14 animate-pulse rounded-full bg-slate-100" />
                          <div className="h-5 w-16 animate-pulse rounded-full bg-slate-100" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : loadError ? (
              <div className="flex h-full items-center justify-center">
                <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-center">
                  <AlertCircle className="mx-auto h-6 w-6 text-red-500" />
                  <p className="mt-2 text-sm font-medium text-red-700">Could not load templates</p>
                  <p className="mt-1 text-xs text-red-600">{loadError}</p>
                </div>
              </div>
            ) : templates.length === 0 ? (
              <div className="flex h-full items-center justify-center" data-testid="template-center-empty-catalog">
                <div className="max-w-md text-center">
                  <Layers className="mx-auto h-8 w-8 text-slate-300" />
                  <p className="mt-2 text-sm font-medium text-slate-700">No templates in this catalog</p>
                  <p className="mt-1 text-xs text-slate-500">
                    System and organization templates are shared across workspaces in your org.
                    Workspace-only templates appear when published for this workspace. If you
                    expected shared templates, try closing and reopening Templates, or ask
                    an admin to publish org templates.
                  </p>
                </div>
              </div>
            ) : kindFilteredTemplates.length === 0 ? (
              <div className="flex h-full items-center justify-center" data-testid="template-center-empty-filter">
                <div className="max-w-md text-center">
                  <Layers className="mx-auto h-8 w-8 text-slate-300" />
                  <p className="mt-2 text-sm font-medium text-slate-700">
                    {kindFilter === 'documents'
                      ? 'No document templates yet'
                      : 'No templates match this view'}
                  </p>
                  <p className="mt-1 text-xs text-slate-500">
                    {kindFilter === 'documents'
                      ? 'Document templates will appear here when your organization publishes them.'
                      : 'Try clearing search or choosing another category. Shared system and org templates remain available across workspaces.'}
                  </p>
                </div>
              </div>
            ) : (
              <>
                {!search.trim() && preferredOrgTemplates.length > 0 ? (
                  <section className="mb-6" data-testid="template-preferred-shelf">
                    <h3 className="text-sm font-semibold text-slate-900">
                      Recommended by your organization
                    </h3>
                    <p className="mt-0.5 text-xs text-slate-500">
                      Curated templates highlighted by your org admin.
                    </p>
                    <div className="mt-3 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                      {preferredOrgTemplates.map((tpl) => (
                        <TemplateCard
                          key={`preferred-${tpl.id}`}
                          template={tpl}
                          currentUserId={currentUserId}
                          isAdmin={isAdmin}
                          canInstantiate={canInstantiate}
                          preferredBusy={preferredBusyId === tpl.id}
                          onPreview={() => handlePreview(tpl)}
                          onUse={() => handleUseTemplate(tpl)}
                          onTogglePreferred={() => handlePreferredToggle(tpl)}
                        />
                      ))}
                    </div>
                  </section>
                ) : null}

                <div className="mb-5 min-h-[2.75rem]">
                  <div className="flex flex-wrap items-end justify-between gap-3">
                    <div>
                      <h3 className="text-base font-semibold text-slate-900">
                        {search.trim() ? 'Search results' : displayLabel || "All templates"}
                      </h3>
                      <p className="mt-0.5 text-sm text-slate-500">
                        {pluralize(gridTemplates.length, "template")}
                        {search.trim() ? ` for “${search.trim()}”` : ""}
                        {isProjectBrowse && complexityFilter !== 'all'
                          ? ` · ${complexityFilter}`
                          : ''}
                      </p>
                    </div>
                    {isProjectBrowse ? (
                      <div
                        className="flex flex-wrap items-center gap-1 rounded-lg border border-slate-200 bg-white p-0.5"
                        data-testid="template-complexity-filter"
                        role="group"
                        aria-label="Filter by setup complexity"
                      >
                        {(
                          [
                            { id: 'all' as const, label: 'All' },
                            { id: 'Simple' as const, label: 'Simple' },
                            { id: 'Standard' as const, label: 'Standard' },
                            { id: 'Advanced' as const, label: 'Advanced' },
                          ] as const
                        ).map((opt) => (
                          <button
                            key={opt.id}
                            type="button"
                            onClick={() => setComplexityFilter(opt.id)}
                            className={`rounded-md px-2.5 py-1 text-[11px] font-medium transition-colors duration-150 ${
                              complexityFilter === opt.id
                                ? 'bg-slate-900 text-white'
                                : 'text-slate-600 hover:bg-slate-50'
                            }`}
                            data-testid={`template-complexity-filter-${opt.id}`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>
                </div>

                {gridTemplates.length === 0 ? (
                  <div
                    className="flex min-h-[280px] flex-1 flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 bg-white px-6 py-16 text-center"
                    data-testid="template-center-search-empty"
                  >
                    <Search className="mb-4 h-12 w-12 text-slate-300" strokeWidth={1.25} aria-hidden />
                    <p className="text-sm font-medium text-slate-700">
                      {emptiedByPreferredShelf
                        ? PREFERRED_SHELF_EMPTY_GRID_COPY
                        : search.trim()
                        ? `No templates found for “${search.trim()}”`
                        : complexityFilter !== 'all'
                          ? `No ${complexityFilter.toLowerCase()} templates in this view`
                        : kindFilter === 'documents'
                          ? 'No document templates in this view'
                          : 'No templates in this department'}
                    </p>
                    <p className="mt-1.5 max-w-sm text-xs text-slate-500">
                      {search.trim()
                        ? isProjectBrowse
                          ? 'Try another keyword, or clear search to browse departments again.'
                          : 'Try another keyword, or clear search to browse documents again.'
                        : complexityFilter !== 'all'
                          ? 'Clear the complexity filter or pick another department.'
                        : isProjectBrowse
                          ? 'Pick another department or clear filters.'
                          : 'Clear filters to see all document templates.'}
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                    {gridTemplates.map((tpl) => (
                      <TemplateCard
                        key={tpl.id}
                        template={tpl}
                        currentUserId={currentUserId}
                        isAdmin={isAdmin}
                        canInstantiate={canInstantiate}
                        preferredBusy={preferredBusyId === tpl.id}
                        onPreview={() => handlePreview(tpl)}
                        onUse={() => handleUseTemplate(tpl)}
                        onTogglePreferred={() => handlePreferredToggle(tpl)}
                      />
                    ))}
                  </div>
                )}
              </>
            )}
          </main>
        </div>
      </div>

      {/* Document preview modal (project Preview uses UseTemplateFlowModal blueprint) */}
      {previewTemplate && (
        <TemplatePreviewModal
          open
          loading={previewLoading}
          error={previewError}
          data={previewData}
          template={previewTemplate}
          onClose={handleClosePreview}
          onUseTemplate={() => {
            if (previewTemplate) {
              handleClosePreview();
              handleUseTemplate(previewTemplate);
            }
          }}
          onAddToProject={
            previewTemplate.kind === 'document' && canInstantiate
              ? () => {
                  const tpl = previewTemplate;
                  handleClosePreview();
                  setAttachTemplate(tpl);
                }
              : undefined
          }
        />
      )}

      {/* TC-F2 multi-step Use Template flow (includes Blueprint step) */}
      <UseTemplateFlowModal
        open={!!pendingTemplate}
        template={pendingTemplate}
        workspaceId={workspaceId || activeWorkspaceId || ''}
        error={instantiateError}
        initialStep={flowInitialStep}
        canInstantiate={canInstantiate}
        onClose={() => {
          setPendingTemplate(null);
          setInstantiateError(null);
        }}
        onSuccess={handleFlowSuccess}
        onError={setInstantiateError}
      />

      <AttachDocumentModal
        open={!!attachTemplate}
        template={attachTemplate}
        workspaceId={workspaceId || activeWorkspaceId || ''}
        onClose={() => setAttachTemplate(null)}
      />
    </>
  );

  if (embedded) {
    return shell;
  }

  return createPortal(
    <>
      <div className="fixed inset-0 z-[5000] bg-black/50" onClick={onClose} />
      {shell}
    </>,
    document.body,
  );
}

/* ── Template card frame ──
 *
 * Required anatomy (TEMPLATE-CENTER-REDESIGN-1):
 *   1. structure-derived preview (real phases / task density)
 *   2. template name
 *   3. one-line purpose
 *   4. setup badge (Simple / Standard / Advanced — derived formula)
 *   5. included structure summary (phase count + task count — never gate counts)
 *   6. Use template (+ Preview)
 *
 * Removed by founder ruling: per-card getting-started doc link.
 * Removed: Coming soon / upcoming shelf cards.
 */
function TemplateCard({
  template,
  currentUserId,
  isAdmin,
  canInstantiate,
  preferredBusy,
  onPreview,
  onUse,
  onTogglePreferred,
}: {
  template: TemplateDto;
  currentUserId: string | null;
  isAdmin: boolean;
  /** workspace_owner | delivery_owner — matches instantiate requireWorkspaceWrite */
  canInstantiate: boolean;
  preferredBusy?: boolean;
  onPreview: () => void;
  onUse: () => void;
  onTogglePreferred?: () => void;
}) {
  const flatPhases = template.phases || [];
  const flatTasks = template.task_templates || template.taskTemplates || [];
  const phaseCount = flatPhases.length;
  const taskCount = flatTasks.length;
  const purpose = templatePurpose(template);
  const setupLevel = deriveComplexity(template);
  const isDocument = template.kind === 'document';
  const setupChip =
    setupLevel === 'Simple'
      ? 'bg-emerald-50 text-emerald-700 border-emerald-100'
      : setupLevel === 'Standard'
        ? 'bg-blue-50 text-blue-700 border-blue-100'
        : 'bg-violet-50 text-violet-700 border-violet-100';

  const usageCount = template.usageCount ?? 0;
  const canTogglePreferred = isAdmin && template.templateScope === 'ORG';
  const useBlocked = !isDocument && !canInstantiate;
  const useDisabled = !isDocument && !canInstantiate;
  const useTitle = useBlocked ? TEMPLATE_INSTANTIATE_DENIED_COPY : undefined;

  return (
    <div
      className={`group flex flex-col overflow-hidden rounded-xl border bg-white shadow-sm transition-all duration-150 ease-in ${
        isDocument
          ? 'border-slate-200 hover:border-slate-300'
          : 'border-slate-200 hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-md'
      }`}
      data-testid={`template-card-${template.id}`}
      data-kind={template.kind}
    >
      {/* Blueprint thumbnail — structure preview, ~40% of card vertical */}
      {!isDocument ? (
        <button
          type="button"
          onClick={onPreview}
          className="relative block h-28 w-full shrink-0 border-b border-slate-100 bg-slate-100 text-left"
          aria-label={`Preview ${template.name}`}
          data-testid={`template-preview-${template.id}`}
        >
          <div className="h-full w-full opacity-90 transition-opacity duration-150 ease-in group-hover:opacity-100">
            <TemplateBlueprint template={template} size="card" />
          </div>
          <span className="pointer-events-none absolute inset-x-0 bottom-2 flex justify-center opacity-0 transition-all duration-150 ease-in group-hover:opacity-100">
            <span className="rounded-md bg-white/95 px-3 py-1 text-xs font-medium text-slate-800 shadow-sm ring-1 ring-slate-200">
              Preview
            </span>
          </span>
        </button>
      ) : (
        <div className="flex h-14 shrink-0 items-center rounded-t-xl border-b border-slate-100 bg-white px-4">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
            Document
          </span>
        </div>
      )}

      <div className="flex flex-1 flex-col p-4">
        <div className="flex items-start justify-between gap-2">
          <h4 className="text-sm font-semibold text-slate-900">{template.name}</h4>
          <div className="flex shrink-0 items-center gap-1">
            {canTogglePreferred ? (
              <button
                type="button"
                disabled={preferredBusy}
                onClick={(e) => {
                  e.stopPropagation();
                  onTogglePreferred?.();
                }}
                title={template.isPreferred ? 'Remove preferred' : 'Mark as preferred'}
                aria-label={template.isPreferred ? 'Remove preferred' : 'Mark as preferred'}
                className="rounded p-0.5 text-amber-400 hover:bg-amber-50 disabled:opacity-50"
                data-testid={`template-preferred-star-${template.id}`}
              >
                <Star
                  className={`h-3.5 w-3.5 ${template.isPreferred ? 'fill-amber-400 text-amber-400' : ''}`}
                />
              </button>
            ) : isAdmin && template.templateScope === 'SYSTEM' ? (
              <span
                title="System templates cannot be marked preferred"
                className="rounded p-0.5 text-slate-300"
                data-testid={`template-preferred-star-${template.id}`}
              >
                <Star className="h-3.5 w-3.5" />
              </span>
            ) : template.isPreferred ? (
              <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" aria-hidden />
            ) : null}
            <span
              className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-500"
              data-testid="template-source-label"
            >
              {sourceLabel(template, currentUserId)}
            </span>
          </div>
        </div>

        {purpose && (
          <p className="mt-1.5 line-clamp-1 text-xs leading-relaxed text-slate-600">
            {purpose}
          </p>
        )}

        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          {!isDocument ? (
            <span
              className={`rounded-full border px-2 py-0.5 text-[10px] font-medium ${setupChip}`}
              data-testid="template-card-setup-badge"
            >
              {setupLevel}
            </span>
          ) : null}
          {usageCount > 0 ? (
            <span
              className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-medium text-slate-600"
              data-testid="template-card-usage-count"
            >
              Used {usageCount}×
            </span>
          ) : null}
        </div>

        <div className="mt-3 flex items-center justify-between">
          <span className="text-[11px] text-slate-400" data-testid="template-card-structure-summary">
            {isDocument
              ? 'Document template'
              : `${phaseCount > 0 ? `${phaseCount} phase${phaseCount !== 1 ? "s" : ""}` : "0 phases"} · ${taskCount > 0 ? `${taskCount} task${taskCount !== 1 ? "s" : ""}` : "0 tasks"}`}
          </span>
          <div className="flex gap-2">
            {isDocument ? (
              <button
                type="button"
                onClick={onPreview}
                className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                data-testid={`template-preview-${template.id}`}
              >
                Preview
              </button>
            ) : (
            <button
              type="button"
              onClick={onUse}
              disabled={useDisabled}
              aria-disabled={useDisabled}
              title={useTitle}
              className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-colors duration-150 ${
                useDisabled
                  ? "cursor-not-allowed bg-slate-100 text-slate-400"
                  : "bg-blue-600 text-white hover:bg-blue-700"
              }`}
              data-testid={`template-use-${template.id}`}
            >
              {useBlocked ? "Use unavailable" : "Use template"}
            </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
